import { useState, useCallback } from "react";
import { ModalPopup } from "../common";

/**
 * Checks if Google Translate is currently active on the page
 * by looking for the 'translated-ltr' or 'translated-rtl' class on <html>
 */
export function isGoogleTranslateActive(): boolean {
  const htmlElement = document.documentElement;
  return (
    htmlElement.classList.contains("translated-ltr") ||
    htmlElement.classList.contains("translated-rtl") ||
    htmlElement.hasAttribute("translate") ||
    !!document.querySelector(".goog-te-banner-frame")
  );
}

/**
 * Checks if an error is a DOM manipulation error commonly caused by
 * browser extensions or translation tools modifying the DOM
 */
export function isDomManipulationError(error: Error): boolean {
  const message = error.message?.toLowerCase() || "";
  const stack = error.stack?.toLowerCase() || "";

  // Common DOM manipulation error patterns
  const domErrorPatterns = [
    "removechild",
    "remove child",
    "not a child",
    "node not found",
    "failed to execute 'removechild'",
    "parameter 1 is not of type 'node'",
    "the node to be removed is not a child of this node",
    "cannot read property 'removechild'",
    "cannot read properties of null (reading 'removechild')",
    "insertbefore",
    "replacechild",
    "mutation",
  ];

  return domErrorPatterns.some(
    (pattern) => message.includes(pattern) || stack.includes(pattern)
  );
}

interface GoogleTranslateWarningModalProps {
  isOpen: boolean;
}

/**
 * Modal component that displays when Google Translate DOM errors are detected.
 * Provides instructions to reload the page and disable translation.
 */
export function GoogleTranslateWarningModal({
  isOpen,
}: GoogleTranslateWarningModalProps) {
  const handleReloadPage = useCallback(() => {
    window.location.reload();
  }, []);

  return (
    <ModalPopup
      isOpen={isOpen}
      onOpenChange={() => {}} // Prevent closing by clicking outside
      title="Issue caused by Browser Extension"
      showCloseIcon={false}
      primaryButtonText="Reload Page"
      onPrimaryButtonClick={handleReloadPage}
      isPrimaryButtonDisabled={false}
      width="SMALL"
    >
      <div className="py-4">
        <p className="mb-4">
          Something on your browser is getting in the way - most often it's
          Google Translate or a similar extension. Try turning it off (or
          disabling automatic translation) and then reload the page to try
          again.
        </p>
        <p className="text-gray-600">
          If you continue to see this message and require assistance please
          contact{" "}
          <a href="mailto:help@korero.io" target="_blank" className="underline">
            help@korero.io
          </a>
        </p>
      </div>
    </ModalPopup>
  );
}

/**
 * Hook for managing Google Translate error detection and modal state.
 * Returns a handler function to be used with ErrorBoundary's onError prop
 * and a modal component to render.
 */
export function useGoogleTranslateErrorHandler() {
  const [showTranslateWarning, setShowTranslateWarning] = useState(false);

  const handleError = useCallback(
    (error: Error, info: { componentStack: string }) => {
      // Check if this is a Google Translate-induced DOM error
      if (isGoogleTranslateActive() && isDomManipulationError(error)) {
        console.warn(
          "Google Translate DOM manipulation error detected:",
          error.message
        );
        setShowTranslateWarning(true);
        // Prevent the error from propagating to the standard error boundary fallback
        return true; // Indicate that we've handled this error
      }

      // For non-translate errors, log normally and let ErrorBoundary handle it
      console.error("Caught error in ErrorBoundary:", error, info);
      return false; // Let ErrorBoundary show its fallback
    },
    []
  );

  return {
    handleError,
    showTranslateWarning,
    TranslateWarningModal: GoogleTranslateWarningModal,
  };
}
